import React, {Fragment, useEffect, useState} from "react";
import {plural, t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {timeLeft} from "./model";
import styles from "./v2.module.css";

/**
 * Où en est la campagne : montant (ou unités, ou nombre de dons), jauge, paliers,
 * doublement. La jauge est annoncée une fois par les lecteurs d'écran (aria-live off) :
 * le rafraîchissement toutes les 5 secondes ne doit pas les faire parler en boucle.
 */
export function GoalProgress({goal, progress, matched, formatMoney, formatNumber, compact = false}) {
    const unit = (n) => (n > 1 ? goal.unitLabel?.other : goal.unitLabel?.one) || ""
    const percent = Math.min(100, Math.max(0, progress.percent))

    let valueText
    let targetText
    if (goal.mode === "UNITS") {
        valueText = `${formatNumber(progress.value, 1)} ${unit(progress.value)}`.trim()
        targetText = `${formatNumber(progress.target, 1)} ${unit(progress.target)}`.trim()
    } else if (goal.mode === "COUNT") {
        valueText = formatNumber(progress.value, 0)
        targetText = plural(progress.target, {one: "# don", other: "# dons"})
    } else {
        valueText = formatMoney(progress.value)
        targetText = formatMoney(progress.target)
    }

    const ariaLabel = progress.target > 0
        ? t`${formatNumber(progress.percent, 0)} % de l'objectif atteint : ${valueText} sur ${targetText}`
        : valueText

    const donations = progress.donations

    return (
        <div>
            <div className={styles.goalValue}>
                <span className={styles.goalAmount}>{valueText}</span>
                {progress.target > 0 && <span className={styles.goalTarget}><Trans>sur {targetText}</Trans></span>}
            </div>

            {progress.target > 0 && (
                <div
                    className={styles.track}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(percent)}
                    aria-label={ariaLabel}
                    aria-live="off"
                >
                    <div className={styles.trackFill} style={{width: percent + "%"}}/>
                </div>
            )}

            <div className={styles.goalMeta}>
                {progress.target > 0 ? <span><Trans>{formatNumber(progress.percent, 0)} % atteint</Trans></span> : <span/>}
                {/* En objectif « nombre de dons », le compte est déjà la valeur principale. */}
                {!compact && goal.mode !== "COUNT" && (
                    <span>{plural(donations, {one: "# don", other: "# dons"})}</span>
                )}
            </div>

            {progress.activeBonus && (
                <p className={styles.badge}>
                    <span className={styles.badgeDot} aria-hidden="true"/>
                    <Trans>Objectif atteint, merci ! Nouveau palier : {goal.mode === "AMOUNT" ? formatMoney(progress.activeBonus) : formatNumber(progress.activeBonus, 0)}</Trans>
                </p>
            )}
            {progress.reached && !progress.activeBonus && (
                <p className={styles.badge}>
                    <span className={styles.badgeDot} aria-hidden="true"/>
                    <Trans>Objectif atteint, merci à tous !</Trans>
                </p>
            )}
            {Number(matched) > 1 && (
                <p className={styles.badge}>
                    <span className={styles.badgeDot} aria-hidden="true"/>
                    {Number(matched) === 2
                        ? <Trans>Chaque don est doublé.</Trans>
                        : <Trans>Chaque don est multiplié par {formatNumber(Number(matched), 1)}.</Trans>}
                </p>
            )}
        </div>
    )
}

function useNow(intervalMs) {
    const [now, setNow] = useState(() => new Date())
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), intervalMs)
        return () => clearInterval(id)
    }, [intervalMs])
    return now
}

/**
 * Temps restant avant l'ouverture ou la clôture. Précision à la minute (mise à jour
 * toutes les 30 s) : un chiffre qui défile chaque seconde fatigue sans rien apprendre.
 */
export function Countdown({life, langValue}) {
    const now = useNow(30000)
    const dateFormat = new Intl.DateTimeFormat(langValue || "fr", {day: "numeric", month: "long"})

    let label
    let target
    if (life.notStarted && life.start) {
        label = t`Ouverture le ${dateFormat.format(life.start)}`
        target = life.start
    } else if (life.end && !life.ended) {
        label = t`Clôture le ${dateFormat.format(life.end)}`
        target = life.end
    } else if (life.end && life.ended && !life.late) {
        return (
            <div className={styles.countdown}>
                <span className={styles.countdownLabel}><Trans>Campagne terminée le {dateFormat.format(life.end)}</Trans></span>
            </div>
        )
    } else {
        // Délai des retardataires (ou campagne permanente) : rien à annoncer, la page
        // reste celle d'une campagne en cours.
        return null
    }

    const left = timeLeft(target, now)
    if (!left) return null

    let value
    if (left.days > 0) {
        value = t`${left.days} j ${String(left.hours).padStart(2, "0")} h ${String(left.minutes).padStart(2, "0")} min`
    } else {
        value = t`${left.hours} h ${String(left.minutes).padStart(2, "0")} min`
    }

    return (
        <div className={styles.countdown}>
            <span className={styles.countdownLabel}>{label}</span>
            {/* Valeur dépendante de l'heure : le serveur et le navigateur peuvent différer d'une minute. */}
            <span className={styles.countdownValue} suppressHydrationWarning>{value}</span>
        </div>
    )
}

export function daysLeftText(life, now = new Date()) {
    if (!life.end || life.ended) return null
    const left = timeLeft(life.end, now)
    if (!left) return null
    if (left.days >= 1) return plural(left.days, {one: "# jour restant", other: "# jours restants"})
    return t`Dernières heures`
}
